import {
  Chart,
  type ChartConfiguration,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import { useEffect, useRef } from 'react';
import { useOtsColorScheme } from '../theme/OtsTheme';
import { otsSeriesColor } from './ots-chart-palette';

// The annotation plugin's defaults only exist once it is registered; per-chart use alone crashes on them.
Chart.register(LineController, LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend, annotationPlugin);

export type OtsTimeSeries = {
  id: string;
  label: string;
  /** Its fixed color slot; by default its place in the list. Keep it fixed when series can be hidden. */
  slot?: number;
  points: { time: number; value: number }[];
  /**
   * `points` draws spot readings as markers alone, so readings days apart don't look like a trend; `trace` is a bare
   * line for dense readings such as SCADA, where a dot per reading is noise.
   */
  style?: 'line' | 'points' | 'trace';
  /** A theme token for the line, e.g. `--ots-transport-category-water`; the series palette otherwise. */
  colorToken?: string;
};

export type OtsTimeSeriesMarker = {
  time: number;
  /** Drawn at the top of the line; an empty label leaves the line bare, for markers too close together to name. */
  label: string;
  /** `danger` draws the line and label in the error color, e.g. a tank drop with no ticket. */
  tone?: 'neutral' | 'danger';
};

export type OtsTimeSeriesChartProps = {
  series: OtsTimeSeries[];
  /** Names the chart for screen readers; a table of the same data should sit beside it. */
  ariaLabel: string;
  /** The one y axis' title, e.g. "Volume (bbl)". */
  valueLabel: string;
  formatValue?: (value: number) => string;
  /** Moments to mark with a vertical line, e.g. when the ticket was written. */
  markers?: OtsTimeSeriesMarker[];
  /** The top of the value axis, e.g. a tank's capacity, so the line reads as how full it is. */
  max?: number;
  height?: number;
  className?: string;
};

// Stable defaults, so the chart is only rebuilt when what it shows changes.
const formatNumber = (value: number) => value.toLocaleString();
const NO_MARKERS: OtsTimeSeriesMarker[] = [];

const cssVar = (element: Element, name: string, fallback: string) =>
  getComputedStyle(element).getPropertyValue(name).trim() || fallback;

/**
 * Values over time as lines, on one axis: 2px lines with markers, recessive grid, a legend when there is more than
 * one series, and a tooltip that follows the pointer along the time axis. Colors come from the fixed series palette
 * for the current light or dark theme; text and grid take the theme's own tokens.
 */
export const OtsTimeSeriesChart = ({
  series,
  ariaLabel,
  valueLabel,
  formatValue = formatNumber,
  markers = NO_MARKERS,
  max,
  height = 260,
  className,
}: OtsTimeSeriesChartProps) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const scheme = useOtsColorScheme() ?? 'light';

  useEffect(() => {
    const element = canvas.current;
    if (!element || typeof window === 'undefined' || !element.getContext?.('2d')) return undefined;
    const text = cssVar(element, '--ots-text-muted', '#64748b');
    const grid = cssVar(element, '--ots-outline-variant', '#e2e8f0');
    const surface = cssVar(element, '--ots-surface', '#ffffff');
    const danger = cssVar(element, '--ots-error', '#dc2626');
    const config: ChartConfiguration<'line', { x: number; y: number }[]> = {
      type: 'line',
      data: {
        datasets: series.map((item, index) => {
          const color = (item.colorToken && cssVar(element, item.colorToken, '')) || otsSeriesColor(item.slot ?? index, scheme);
          return {
            label: item.label,
            data: item.points.map(({ time, value }) => ({ x: time, y: value })),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            showLine: item.style !== 'points',
            pointRadius: item.style === 'points' ? 5 : item.style === 'trace' ? 0 : 3,
            pointHoverRadius: item.style === 'trace' ? 3 : 5,
            // A ring of the surface around each marker keeps crossing lines readable.
            pointBorderColor: surface,
            pointBorderWidth: 1,
            tension: 0,
          };
        }),
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        scales: {
          x: { type: 'time', ticks: { color: text, maxRotation: 0, autoSkipPadding: 16 }, grid: { color: grid, display: false }, border: { color: grid } },
          y: {
            title: { display: true, text: valueLabel, color: text },
            ticks: { color: text, callback: (value) => formatValue(Number(value)) },
            grid: { color: grid },
            border: { display: false },
            beginAtZero: true,
            max,
          },
        },
        plugins: {
          // ots-ui's Chart registers chartjs-plugin-datalabels for every chart in the app; it would print every point's value.
          ...({ datalabels: { display: false } } as object),
          legend: { display: series.length > 1, position: 'top', align: 'start', labels: { color: text, boxWidth: 12, boxHeight: 2 } },
          tooltip: { callbacks: { label: (item) => `${item.dataset.label}: ${formatValue(item.parsed.y)}` } },
          annotation: {
            annotations: Object.fromEntries(markers.map((marker, index) => [`marker${index}`, {
              type: 'line' as const,
              scaleID: 'x',
              value: marker.time,
              borderColor: marker.tone === 'danger' ? danger : text,
              borderWidth: 1,
              borderDash: [4, 4],
              label: {
                display: marker.label !== '',
                content: marker.label,
                position: 'start' as const,
                color: marker.tone === 'danger' ? danger : text,
                backgroundColor: surface,
                font: { size: 11 },
              },
            }])),
          },
        },
      },
    };
    const chart = new Chart(element, config);
    return () => chart.destroy();
  }, [series, scheme, valueLabel, formatValue, markers, max]);

  return (
    <div className={['relative w-full', className].filter(Boolean).join(' ')} style={{ height }}>
      <canvas ref={canvas} role="img" aria-label={ariaLabel}/>
    </div>
  );
};
